import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import PageTitle from './PageTitle';
import SponsorButton from './SponsorButton';
import * as FiIcons from 'react-icons/fi';

const { FiSun, FiMoon, FiGlobe } = FiIcons;

const Header = ({ activeTab, setActiveTab }) => {
  const { t, language, changeLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();

  const tabs = [
    {
      id: 'metronome',
      label: t('tabMetronome'),
      icon: '🎵',
      ariaLabel: t('ariaMetronome'),
    },
    {
      id: 'character',
      label: t('tabCharacter'),
      icon: '🦄',
      ariaLabel: t('ariaCharacter'),
    },
    {
      id: 'achievements',
      label: t('tabAchievements'),
      icon: '🏆',
      ariaLabel: t('ariaAchievements'),
    },
  ];

  const handleLanguageToggle = () => {
    changeLanguage(language === 'zh' ? 'en' : 'zh');
  };

  const rainbowStyle = {
    background: 'linear-gradient(90deg,#FF6EC4,#FFD600,#42E695,#6A82FB,#EA5C54,#FFD600,#FF6EC4)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    fontWeight: 700,
    textShadow: isDark ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
    animation: 'rainbow 6s linear infinite',
  };

  return (
    <header className="text-center relative z-20">
      <div className="flex justify-between items-center mb-4">
        <SponsorButton />

        <div className="flex items-center space-x-2">
          <button
            onClick={handleLanguageToggle}
            className="p-2 rounded-xl bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all duration-200 flex items-center space-x-2"
            title={language === 'zh' ? 'Switch to English' : '切換到中文'}
            aria-label={language === 'zh' ? 'Switch to English' : '切換到中文'}
          >
            <SafeIcon
              icon={FiGlobe}
              className="w-4 h-4 text-gray-600 dark:text-gray-300"
            />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {language === 'zh' ? 'EN' : '中文'}
            </span>
          </button>

          <button
            onClick={toggleTheme}
            className="p-2 rounded-xl bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all duration-200"
            title={
              isDark
                ? language === 'zh'
                  ? '切換到淺色模式'
                  : 'Switch to Light Mode'
                : language === 'zh'
                  ? '切換到深色模式'
                  : 'Switch to Dark Mode'
            }
            aria-label={isDark ? '切換到淺色模式' : '切換到深色模式'}
          >
            <SafeIcon
              icon={isDark ? FiSun : FiMoon}
              className="w-4 h-4 text-gray-600 dark:text-gray-300"
            />
          </button>
        </div>
      </div>

      <div className="mb-8 max-w-xl mx-auto">
        <PageTitle
          className="text-5xl sm:text-6xl mb-3 relative z-10"
          id="main-heading"
        >
          <span style={rainbowStyle}>Beat Buddy</span>
        </PageTitle>
        <p className="text-lg relative z-10 text-gray-700 dark:text-gray-300 mx-auto max-w-md">
          {t('appSubtitle')}
        </p>
      </div>

      <nav aria-label="主導航">
        <div className="flex justify-center bg-white dark:bg-gray-800 rounded-2xl p-2 shadow-lg max-w-sm sm:max-w-md mx-auto overflow-hidden relative z-10">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-3 rounded-xl font-medium transition-all duration-200 flex-1 text-center ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-current={activeTab === tab.id ? 'page' : undefined}
              aria-label={tab.ariaLabel}
            >
              <span className="text-base sm:text-lg" aria-hidden="true">
                {tab.icon}
              </span>
              <span className="text-xs sm:text-sm font-medium truncate">
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
    </header>
  );
};

export default Header;